import React, { useEffect, useState } from "react";
import _ from "lodash";
import Select, { SingleValue } from "react-select";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { EventInventoryRecord, selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { systemActions } from "@src/store";
import { getSuggestedTags } from "@src/store/system/suggestedTags";
import { removeListingsTagsLocally } from "@src/store/system/removeTag";
import { Button, ButtonVariant } from "../button/Button";
import { filtersConfig, editCustomComponents, OptionType } from "../utils/reactSelectUtils";
import helperStyles from "@src/components/helpers.module.scss";
import { clx } from "../utils/stringUtils";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import BulkTagItem from "./BulkTagItem";
import { hasTag, listingsMissingTag, matchingTagIds, TAG_DELIMITER, unsoldItemIds } from "./bulkTag.utils";
import styles from "./BulkTag.module.scss";

type TBulkTag = {
	handleClose?: () => void;
};

const LOOKUP_BATCH_SIZE = 5;

const pluralize = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

const BulkTag = ({ handleClose }: TBulkTag) => {
	const dispatch = useAppDispatch();

	const selectedItems = useAppSelector(selectSelectedInventoryItems);
	const inventory = useAppSelector((state) => state.pricing.eventInventoryAll.data);
	const theme = useAppSelector((state) => state.settings.theme);
	const { data: suggestions, loading: suggestionsLoading } = useAppSelector((state) => state.system.suggestedTags);

	const [items, setItems] = useState<Array<EventInventoryRecord>>(selectedItems);
	const [tags, setTags] = useState<Array<string>>([]);
	const [removals, setRemovals] = useState<Record<number, Array<string>>>({});
	const [text, setText] = useState("");
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (loading) return;
		setItems(selectedItems);
	}, [selectedItems, loading]);

	useEffect(() => {
		if (text.trim().length < 1) return;
		const res = dispatch(getSuggestedTags({ suggestion: text.trim() }));
		return () => res.abort();
	}, [dispatch, text]);

	const addTag = (value: string) => {
		const tag = value.trim();
		if (!tag) return;
		if (tag.includes(TAG_DELIMITER)) {
			toast.warning(`Tags can't contain "${TAG_DELIMITER}".`);
			return;
		}
		setText("");
		if (hasTag(tags, tag)) return;
		setTags((prev) => [...prev, tag]);
	};

	const removeTag = (tag: string) => setTags((prev) => prev.filter((x) => x !== tag));

	const handleRemoveItem = (listingId: number) => {
		setItems((prev) => prev.filter((x) => x.Listing_ID !== listingId));
		setRemovals((prev) => _.omit(prev, listingId));
	};

	const handleToggleExistingTag = (listingId: number, tag: string) => {
		setRemovals((prev) => {
			const current = prev[listingId] ?? [];
			const next = current.includes(tag) ? current.filter((x) => x !== tag) : [...current, tag];
			return next.length ? { ...prev, [listingId]: next } : _.omit(prev, listingId);
		});
	};

	const activeRemovals = _.pickBy(removals, (_tags, listingId) => items.some((x) => x.Listing_ID === Number(listingId)));
	const hasRemovals = Object.keys(activeRemovals).length > 0;

	const tagOptions = (suggestions || []).filter((x) => !hasTag(tags, x)).map((x) => ({ label: x, value: x }));

	const lookupRemovalTagIds = async (targetRemovals: Record<number, Array<string>>) => {
		const listingIds = Object.keys(targetRemovals).map(Number);
		const lookups: Array<PromiseSettledResult<{ listingId: number; tagIds: Array<number> }>> = [];
		for (const batch of _.chunk(listingIds, LOOKUP_BATCH_SIZE)) {
			const settled = await Promise.allSettled(
				batch.map((listingId) =>
					dispatch(systemActions.getListingTags({ listingIds: [listingId], skipCache: true, skipSavingCache: true }))
						.unwrap()
						.then((listingTags) => ({ listingId, tagIds: matchingTagIds(listingTags, targetRemovals[listingId]) })),
				),
			);
			lookups.push(...settled);
		}
		return lookups;
	};

	const applyRemovals = async (targetRemovals: Record<number, Array<string>>) => {
		const listingIds = Object.keys(targetRemovals).map(Number);
		if (!listingIds.length) return { removedListingIds: [] as Array<number>, failedCount: 0, unmatchedCount: 0 };

		const lookups = await lookupRemovalTagIds(targetRemovals);
		const resolved = lookups.flatMap((x) => (x.status === "fulfilled" ? [x.value] : []));
		const matched = resolved.filter((x) => x.tagIds.length);
		const failedCount = lookups.length - resolved.length;
		const unmatchedCount = resolved.length - matched.length;
		if (!matched.length) return { removedListingIds: [], failedCount, unmatchedCount };

		try {
			await dispatch(
				systemActions.removeTag({ tagIds: matched.flatMap((x) => x.tagIds), skipCache: true, skipSavingCache: true }),
			).unwrap();
			const removedListingIds = matched.map((x) => x.listingId);
			dispatch(removeListingsTagsLocally(_.pick(targetRemovals, removedListingIds)));
			return { removedListingIds, failedCount, unmatchedCount };
		} catch {
			return { removedListingIds: [], failedCount: failedCount + matched.length, unmatchedCount };
		}
	};

	const handleApply = async () => {
		const targets = items;
		const targetRemovals = activeRemovals;
		if (!targets.length || (!tags.length && !hasRemovals)) return;

		setLoading(true);
		const failures: Array<string> = [];
		const updatedListingIds = new Set<number>();

		const { removedListingIds, failedCount, unmatchedCount } = await applyRemovals(targetRemovals);
		removedListingIds.forEach((id) => updatedListingIds.add(id));
		if (failedCount) failures.push(`removing tags from ${pluralize(failedCount, "listing")}`);
		if (unmatchedCount) failures.push(`finding the tags to remove on ${pluralize(unmatchedCount, "listing")}`);

		for (const tag of tags) {
			const missing = listingsMissingTag(targets, tag, targetRemovals);
			if (!missing.length) continue;
			try {
				await dispatch(
					systemActions.addItemTags({
						ItemIds: missing.flatMap((x) => unsoldItemIds(inventory, x)),
						TagDescriptions: [tag],
						skipCache: true,
						skipSavingCache: true,
					}),
				).unwrap();
				missing.forEach((x) => updatedListingIds.add(x.Listing_ID));
			} catch {
				failures.push(`adding "${tag}" to ${pluralize(missing.length, "listing")}`);
			}
		}

		setLoading(false);

		if (!failures.length) {
			if (updatedListingIds.size) {
				toast.success(`Tags updated on ${pluralize(updatedListingIds.size, "listing")}.`);
			} else {
				toast.info("Selected listings already have these tags.");
			}
			handleClose?.();
			return;
		}

		const description = `Failed ${failures.join(", ")}.`;
		if (!updatedListingIds.size) {
			toast.error("Failed to update tags.", { description });
			return;
		}
		toast.warning("Some tags could not be updated.", { description });
		handleClose?.();
	};

	const mappedItems = items.map((x) => (
		<BulkTagItem
			key={x.Item_ID}
			data={x}
			newTags={tags}
			removedTags={removals[x.Listing_ID] ?? []}
			onToggleExistingTag={loading ? undefined : handleToggleExistingTag}
			onRemove={loading ? undefined : handleRemoveItem}
		/>
	));

	return (
		<div className={styles.root}>
			<EventContextHeader padding listings={items.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))} />
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={clx(styles.subHeader, styles.mb)}>Tags</div>
					<div className={styles.fieldLabel}>Add tags</div>
					<div className={styles.searchRow}>
						<div className={styles.select}>
							<Select<OptionType, false>
								value={null}
								isLoading={suggestionsLoading}
								inputValue={text}
								onInputChange={(val, meta) => {
									if (meta.action === "input-change") setText(val);
								}}
								onChange={(option: SingleValue<OptionType>) => {
									if (option) addTag(String(option.value));
								}}
								options={tagOptions}
								isSearchable
								isDisabled={loading}
								placeholder="Start typing..."
								noOptionsMessage={() => (text.trim() ? "Press Enter to add" : "Start typing...")}
								styles={filtersConfig("", theme, undefined, 170)}
								components={editCustomComponents}
								onKeyDown={(e) => {
									if (e.key === "Enter" && text.trim()) {
										addTag(text);
										e.preventDefault();
									}
								}}
							/>
						</div>
						<Button onClick={() => addTag(text)} variant={ButtonVariant.Secondary} disabled={loading || !text.trim()}>
							Add
						</Button>
					</div>
					<div className={styles.helperText}>
						Tags are added to every target listing. To remove an existing tag, click its X in Target Listings.
					</div>
					{tags.length ? (
						<div className={styles.tagContainer}>
							{tags.map((x) => (
								<div key={x} className={styles.tag}>
									<div className={styles.tagLabel}>{x}</div>
									<div className={styles.tagRemove} onClick={() => !loading && removeTag(x)}>
										<X size={14} />
									</div>
								</div>
							))}
						</div>
					) : (
						<div className={styles.emptyTags}>No tags added yet.</div>
					)}
				</div>
				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Target Listings</div>
						<div className={styles.subHeaderSecondary}>Applying to {pluralize(items.length, "listing")}</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={clx(styles.secRowSeat, styles.headerSecRowSeat)}>
							<div className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>Sec, Row, Seat</div>
						</div>
						<div className={styles.tagsColumn}>Tags</div>
						<div className={styles.qty}>Qty</div>
						<div className={styles.priceLabel}>Price</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleApply}
					isLoading={loading}
					disabled={!items.length || (!tags.length && !hasRemovals) || loading}>
					Apply tags
				</Button>
			</div>
		</div>
	);
};

export default BulkTag;
